import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Search,
  Ship,
  Truck,
  Loader2,
  Check,
  X,
  Pencil,
  AlertTriangle,
  Info,
  ChevronDown,
  History,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import { useToast } from '@/lib/hooks/use-toast';
import { tradeService } from '../services/trade-service';
import type { EditableRate } from '../types/trade.types';
import { formatShortDate, toDateInputValue } from '../utils/trade-dates';

/**
 * Las tarifas del tarifario, editables en línea.
 *
 * Es lo que despega a Delicia del Excel. El Excel sigue siendo la puerta de
 * entrada semanal —son 390 filas, cargarlas a mano no tiene sentido— pero
 * corregir UNA tarifa no debería obligar a reimportar todo el archivo.
 *
 * Solo se editan monto, vigencia y días. Naviera, origen, destino y equipo son
 * de solo lectura a propósito: cambiar cualquiera de esos no es corregir esta
 * tarifa, es otra tarifa distinta. Si se editaran, el historial diría que el
 * precio de Shanghái cambió cuando en realidad se lo apuntó a otro puerto.
 */

type LegFilter = 'INTERNATIONAL' | 'INLAND_DESTINATION' | '';

const LEG_TABS: Array<[LegFilter, string, typeof Ship]> = [
  ['INTERNATIONAL', 'Marítimas', Ship],
  ['INLAND_DESTINATION', 'Terrestres', Truck],
  ['', 'Todas', Ship],
];

const money = (n: number | null, currency = 'USD') =>
  n === null
    ? '—'
    : `${currency} ${n.toLocaleString('es-BO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const shortDate = (iso: string | null) => formatShortDate(iso, 'sin fecha');

/**
 * `refreshKey` cambia y la tabla vuelve a leer.
 *
 * 🔴 Hace falta porque importar el tarifario **borra y recrea** todas las
 * tarifas: es un reemplazo completo, no una actualización. Los ids que la tabla
 * tenía en memoria dejan de existir, así que al editar una fila el servidor
 * contestaba "la tarifa no existe" y solo se arreglaba recargando la página a
 * mano. El síntoma era desconcertante —acababas de ver esa fila— y el motivo
 * invisible.
 */
export function RatesTable({ refreshKey }: { refreshKey?: number }) {
  const { toast } = useToast();
  const [legType, setLegType] = useState<LegFilter>('INTERNATIONAL');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [rates, setRates] = useState<EditableRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  /**
   * Apilar debajo las tarifas que reemplazaron cargas anteriores del Excel.
   *
   * **Arranca apagado a propósito.** El uso diario de esta tabla es "¿con qué
   * puedo cotizar hoy?", y cada carga del Excel archiva 390 filas: al tercer mes
   * las vigentes quedarían enterradas bajo mil viejas. Con el interruptor, el
   * historial completo está a un clic y el trabajo de todos los días no cambia.
   */
  const [conHistoricas, setConHistoricas] = useState(false);
  const [conteo, setConteo] = useState({ vigentes: 0, historicas: 0 });

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await tradeService.listRates({
        legType: legType || undefined,
        q: debounced || undefined,
        historicas: conHistoricas || undefined,
      });
      setRates(r.rates);
      if (r.conteo) setConteo(r.conteo);
    } catch {
      setRates([]);
    } finally {
      setLoading(false);
    }
  }, [legType, debounced, conHistoricas]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  /** Reemplaza una fila sin recargar toda la tabla. */
  const applyPatch = (id: string, patch: Partial<EditableRate>) =>
    setRates((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  // Solo cuenta las vigentes: una tarifa archivada está vencida por definición,
  // y sumarla al aviso de "112 vencidas" lo volvería un número sin significado.
  const expiredCount = useMemo(
    () => rates.filter((r) => r.isExpired && !r.esHistorica).length,
    [rates]
  );

  return (
    <div className="space-y-3">
      {/* Filtros */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Bajan de renglón en el celular: a 390 px «Ver también históricas» se salía del ancho. */}
        <div className="flex flex-wrap gap-1.5">
          {LEG_TABS.map(([key, label, Icon]) => (
            <button
              key={label}
              type="button"
              onClick={() => setLegType(key)}
              aria-pressed={legType === key}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                legType === key
                  ? 'border-prixia-ink bg-prixia-ink text-white'
                  : 'border-border hover:border-prixia-silver'
              )}
            >
              <Icon className="h-3 w-3" />
              {label}
            </button>
          ))}

          {/*
            El historial se enciende acá, al lado de los filtros de tipo, porque
            es una dimensión más de lo mismo: qué filas se ven. Ponerlo en otro
            lado lo volvería una pantalla aparte, que es justo lo que no se
            quería — el tarifario tiene que leerse de corrido, lo de hoy arriba y
            lo de antes abajo.
          */}
          <button
            type="button"
            onClick={() => setConHistoricas((v) => !v)}
            aria-pressed={conHistoricas}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              conHistoricas
                ? 'border-alerta-borde bg-alerta-suave text-foreground'
                : 'border-border hover:border-prixia-silver'
            )}
          >
            <History className="h-3 w-3" />
            {conHistoricas ? 'Ocultar históricas' : 'Ver también históricas'}
            {conHistoricas && conteo.historicas > 0 && (
              <span className="tabular-nums opacity-70">· {conteo.historicas}</span>
            )}
          </button>
        </div>

        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Naviera, puerto o ciudad"
            className="h-9 pl-9 text-sm"
            aria-label="Buscar tarifa"
          />
        </div>
      </div>

      {!loading && expiredCount > 0 && (
        <p className="flex items-start gap-1.5 rounded-md border border-alerta-borde bg-alerta-suave px-3 py-2 text-xs text-foreground">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-alerta" />
          <span>
            <span className="font-semibold">{expiredCount} de estas tarifas están vencidas.</span>{' '}
            Se puede extender la vigencia acá mismo si el proveedor la confirmó, o subir el
            tarifario nuevo.
          </span>
        </p>
      )}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-11 w-full" />
          ))}
        </div>
      ) : rates.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center">
          <Ship className="h-8 w-8 text-prixia-silver" />
          <p className="text-sm font-medium">
            {debounced ? `Nada que coincida con «${debounced}»` : 'Todavía no hay tarifas cargadas'}
          </p>
          {!debounced && (
            <p className="max-w-sm text-xs text-muted-foreground">
              Sube el Excel del tarifario arriba. Después puedes corregirlas una por una desde acá.
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-muted/50">
              <tr className="border-b text-xs">
                <th className="px-3 py-2 text-left font-medium">Proveedor</th>
                <th className="px-3 py-2 text-left font-medium">Tramo</th>
                <th className="px-3 py-2 text-left font-medium">Equipo</th>
                <th className="px-3 py-2 text-right font-medium">Costo</th>
                <th className="px-3 py-2 text-left font-medium">Vigencia</th>
                <th className="px-3 py-2 text-right font-medium">Días</th>
                <th className="w-20 px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rates.map((rate) =>
                editing === rate.id ? (
                  <EditRow
                    key={rate.id}
                    rate={rate}
                    onCancel={() => setEditing(null)}
                    onSaved={(patch, breakdownOutdated) => {
                      applyPatch(rate.id, patch);
                      setEditing(null);
                      toast({
                        title: 'Tarifa actualizada',
                        description: breakdownOutdated
                          ? 'El monto cambió, así que el desglose ya no lo explica. Revisa los conceptos cuando puedas.'
                          : 'Ya se cotiza con el valor nuevo.',
                        variant: breakdownOutdated ? 'warning' : 'success',
                      });
                    }}
                    onError={(message) =>
                      toast({
                        title: 'No se pudo guardar',
                        description: message,
                        variant: 'destructive',
                      })
                    }
                  />
                ) : (
                  <ReadRow
                    key={rate.id}
                    rate={rate}
                    isExpanded={expanded === rate.id}
                    onToggle={() => setExpanded(expanded === rate.id ? null : rate.id)}
                    onEdit={() => setEditing(rate.id)}
                  />
                )
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && rates.length >= 600 && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Se muestran las primeras 600. Usa el buscador para llegar a una en particular.
        </p>
      )}
    </div>
  );
}

// ── Fila de lectura ──────────────────────────────────────────────────────────

function ReadRow({
  rate,
  isExpanded,
  onToggle,
  onEdit,
}: {
  rate: EditableRate;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
}) {
  const componentSum = rate.components.reduce((a, c) => a + c.amount, 0);
  const gap = rate.amount === null ? 0 : rate.amount - componentSum;

  return (
    <>
      {/*
        Las archivadas se distinguen con fondo y texto atenuado, no con un ícono.

        Van mezcladas con las vigentes en una sola lista, así que la diferencia
        tiene que notarse de un vistazo mientras se recorre la tabla — un ícono
        chico obliga a leer fila por fila para no confundir un precio viejo con
        uno que se puede cotizar hoy.
      */}
      <tr
        className={cn(
          'border-b transition-colors last:border-0 hover:bg-muted/30',
          rate.esHistorica && 'bg-muted/40 text-muted-foreground'
        )}
      >
        <td className="px-3 py-2.5">
          <p className="text-xs font-medium">{rate.providerName}</p>
          {rate.esHistorica && rate.reemplazadaEl && (
            <p className="text-[10px] text-alerta" title={rate.archivo}>
              reemplazada el {shortDate(rate.reemplazadaEl)}
            </p>
          )}
          {rate.serviceType && (
            <p className="text-[11px] text-muted-foreground">{rate.serviceType}</p>
          )}
          {rate.agentName && (
            <p className="text-[11px] text-muted-foreground">agente {rate.agentName}</p>
          )}
          {rate.requiresCarrierName && (
            <p className="text-[11px] text-prixia-blue">
              solo para carga de {rate.requiresCarrierName}
            </p>
          )}
        </td>

        <td className="px-3 py-2.5 text-xs">
          {rate.origin} <span className="text-prixia-silver">→</span> {rate.destination}
        </td>

        <td className="px-3 py-2.5">
          {rate.equipment ? (
            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">
              {rate.equipment}
            </span>
          ) : (
            <span className="text-[11px] text-muted-foreground">—</span>
          )}
        </td>

        <td className="px-3 py-2.5 text-right">
          <button
            type="button"
            onClick={onToggle}
            disabled={rate.components.length === 0}
            className={cn(
              'font-mono text-xs font-semibold tabular-nums',
              rate.components.length > 0 && 'text-prixia-blue hover:underline'
            )}
            title={rate.components.length > 0 ? 'Ver el desglose' : undefined}
          >
            {money(rate.amount, rate.currency)}
            {rate.components.length > 0 && (
              <ChevronDown
                className={cn(
                  'ml-1 inline h-3 w-3 transition-transform',
                  isExpanded && 'rotate-180'
                )}
              />
            )}
          </button>
        </td>

        <td className="px-3 py-2.5">
          <span
            className={cn(
              'inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium',
              rate.isExpired ? 'bg-bloqueo-suave text-bloqueo' : 'bg-ok-suave text-ok'
            )}
          >
            {rate.isExpired ? 'venció' : 'hasta'} {shortDate(rate.validUntil)}
          </span>
          {rate.maxWeightKg !== null && (
            <p className="mt-0.5 text-[10px] text-muted-foreground">
              tope {rate.maxWeightKg.toLocaleString('es-BO')} kg
            </p>
          )}
        </td>

        <td className="px-3 py-2.5 text-right font-mono text-xs tabular-nums text-muted-foreground">
          {rate.estimatedDays ?? '—'}
        </td>

        <td className="px-3 py-2.5 text-right">
          {/*
            Una tarifa archivada no se edita: es lo que se cobró y punto. El
            backend también la rechaza —el id ni siquiera existe como tarifa
            viva— así que acá directamente no se ofrece.
          */}
          {rate.esHistorica ? (
            <span className="text-[10px] text-muted-foreground">archivada</span>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 gap-1 px-2 text-[11px]"
              onClick={onEdit}
              title="Corregir esta tarifa"
            >
              <Pencil className="h-3 w-3" />
              Editar
            </Button>
          )}
        </td>
      </tr>

      {isExpanded && (
        <tr className="border-b bg-muted/20">
          <td colSpan={7} className="px-3 py-2.5">
            <div className="ml-auto max-w-md space-y-1">
              {rate.components.map((c, i) => (
                <div key={i} className="flex justify-between gap-4 text-xs">
                  <span className="text-muted-foreground">{c.concept}</span>
                  <span className="font-mono tabular-nums">{c.amount.toLocaleString('es-BO')}</span>
                </div>
              ))}
              {/*
                El tarifario tiene el total a mano y las columnas de conceptos
                llenas a medias. Se muestra la diferencia en vez de un desglose
                que no cuadra.
              */}
              {Math.abs(gap) > 0.01 && (
                <div className="flex justify-between gap-4 text-xs text-prixia-blue">
                  <span>Otros cargos sin desglosar en el tarifario</span>
                  <span className="font-mono tabular-nums">{gap.toLocaleString('es-BO')}</span>
                </div>
              )}
              <div className="flex justify-between gap-4 border-t pt-1 text-xs font-semibold">
                <span>Total</span>
                <span className="font-mono tabular-nums">
                  {(rate.amount ?? 0).toLocaleString('es-BO')}
                </span>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Fila en edición ──────────────────────────────────────────────────────────

function EditRow({
  rate,
  onCancel,
  onSaved,
  onError,
}: {
  rate: EditableRate;
  onCancel: () => void;
  onSaved: (patch: Partial<EditableRate>, breakdownOutdated: boolean) => void;
  onError: (message: string) => void;
}) {
  const [amount, setAmount] = useState(String(rate.amount ?? ''));
  const [validUntil, setValidUntil] = useState(toDateInputValue(rate.validUntil));
  const [days, setDays] = useState(rate.estimatedDays === null ? '' : String(rate.estimatedDays));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const result = await tradeService.updateRate(rate.id, {
        amount: Number(amount),
        validUntil: validUntil || null,
        estimatedDays: days === '' ? null : Number(days),
      });
      onSaved(
        {
          amount: result.rate.amount,
          validUntil: result.rate.validUntil,
          estimatedDays: days === '' ? null : Number(days),
          // La vigencia la dicta el servidor, con la misma regla que usa el
          // cotizador. Recalcularla acá abría la puerta a que la fila dijera
          // "vigente" mientras el cotizador la descartaba.
          isExpired: result.rate.isExpired,
        },
        result.breakdownOutdated
      );
    } catch (err) {
      onError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          'Ocurrió un error al guardar.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="border-b bg-prixia-sky/10">
      {/* Proveedor y tramo NO se editan: cambiarlos sería otra tarifa. */}
      <td className="px-3 py-2 text-xs font-medium">{rate.providerName}</td>
      <td className="px-3 py-2 text-xs">
        {rate.origin} → {rate.destination}
      </td>
      <td className="px-3 py-2">
        {rate.equipment && (
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">
            {rate.equipment}
          </span>
        )}
      </td>

      <td className="px-3 py-2">
        <Input
          type="number"
          min={0}
          step="1"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="h-8 text-right font-mono text-xs"
          aria-label="Costo"
          autoFocus
        />
      </td>

      <td className="px-3 py-2">
        <Input
          type="date"
          value={validUntil}
          onChange={(e) => setValidUntil(e.target.value)}
          className="h-8 text-xs"
          aria-label="Vigencia hasta"
        />
      </td>

      <td className="px-3 py-2">
        <Input
          type="number"
          min={0}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="h-8 text-right font-mono text-xs"
          aria-label="Días de tránsito"
        />
      </td>

      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            className="h-7 w-7 p-0"
            disabled={saving || amount === '' || Number(amount) <= 0}
            onClick={() => void save()}
            title="Guardar"
            aria-label="Guardar la tarifa"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            disabled={saving}
            onClick={onCancel}
            title="Cancelar"
            aria-label="Cancelar la edición"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}
